import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { Type } from 'lucide-react';

import CommonLoader from '../../../../../components/common-loader/CommonLoader';
import ConfirmationModal from '../../../../../components/confirmation-modal/ConfirmationModal';
import EmptyState from '../../../../../components/empty-state/EmptyState';
import { MEDIA_STUDIO_ASSET_PREVIEW_TYPES } from '../../../../../constants';
import DeleteLabel from '../../../../../projects/projects-list/components/DeleteLabel';
import {
  deleteMediaStudioFont,
  getMediaStudioFonts,
  loadMediaStudioFontFile,
  uploadMediaStudioFont,
} from '../../../../../redux/settings/media-studio/mediaStudioSettingsActions';
import usePendingFonts from '../../hooks/usePendingFonts';
import AssetCard from '../asset-card/AssetCard';
import AssetDropArea from '../asset-drop-area/AssetDropArea';
import AssetGrid from '../asset-grid/AssetGrid';
import AssetPreviewModal from '../asset-preview-modal/AssetPreviewModal';
import FontNamingModal from '../font-naming-modal/FontNamingModal';

import scss from './FontAssets.module.scss';

const FONT_ACCEPT = {
  'font/otf': ['.otf'],
  'font/ttf': ['.ttf'],
  'font/collection': ['.ttc'],
  'font/sfnt': ['.sfnt'],
  'font/woff': ['.woff'],
  'font/woff2': ['.woff2'],
};

const getFontStack = (family) => (family ? `"${family}", sans-serif` : undefined);

function FontAssets() {
  const { t } = useTranslation('settings');
  const dispatch = useDispatch();
  const { fonts, isFontsLoading, uploadingFonts, deletingFontIds, fontFamilies, loadingFontFileIds } = useSelector(
    (state) => state.mediaStudioSettings,
  );
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleFontsError = useCallback(() => setError('mediaStudioFontFailed'), []);
  const handleSaveFonts = useCallback(
    (items) => items.forEach((item) => dispatch(uploadMediaStudioFont(item))),
    [dispatch],
  );
  const { pendingFonts, addFiles, renameFont, saveFonts, discardFonts } = usePendingFonts({
    onError: handleFontsError,
    onSave: handleSaveFonts,
  });

  useEffect(() => {
    dispatch(getMediaStudioFonts());
  }, [dispatch]);

  useEffect(() => {
    fonts.forEach((font) => dispatch(loadMediaStudioFontFile(font.id)));
  }, [dispatch, fonts]);

  const handleFiles = (files) => {
    setError(null);
    addFiles(files);
  };

  const handleRejected = (rejections) => {
    const isTooMany = rejections.some(({ errors }) => errors.some(({ code }) => code === ErrorCode.TooManyFiles));
    setError(isTooMany ? 'mediaStudioFontSingle' : 'mediaStudioFontUnsupported');
  };

  const renderContent = () => {
    if (isFontsLoading) {
      return <CommonLoader isLoading />;
    }

    if (!fonts.length && !uploadingFonts.length) {
      return (
        <EmptyState
          icon={<Type size={28} strokeWidth={1.5} />}
          title={t('mediaStudioNoFonts')}
          description={t('mediaStudioNoFontsHint')}
        />
      );
    }

    return (
      <AssetGrid>
        {fonts.map((font) => {
          const stack = getFontStack(fontFamilies[font.id]);

          return (
            <AssetCard
              key={font.id}
              title={font.label}
              isLoading={deletingFontIds.includes(font.id) || loadingFontFileIds.includes(font.id)}
              onPreview={() =>
                setPreview({ type: MEDIA_STUDIO_ASSET_PREVIEW_TYPES.FONT, item: { label: font.label, stack } })
              }
              onDelete={() => setDeleteTarget(font)}>
              <span className={scss.glyphs} style={{ fontFamily: stack }}>
                Aa
              </span>
              <span className={scss.sample} style={{ fontFamily: stack }}>
                {t('mediaStudioFontSample')}
              </span>
            </AssetCard>
          );
        })}
        {uploadingFonts.map((font) => (
          <AssetCard key={font.id} title={font.label} isLoading />
        ))}
      </AssetGrid>
    );
  };

  return (
    <AssetDropArea
      accept={FONT_ACCEPT}
      multiple={false}
      disabled={Boolean(uploadingFonts.length || pendingFonts.length)}
      error={error}
      onErrorClose={() => setError(null)}
      onFiles={handleFiles}
      onRejected={handleRejected}>
      {renderContent()}

      <AssetPreviewModal asset={preview} onClose={() => setPreview(null)} />

      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => dispatch(deleteMediaStudioFont(deleteTarget.id))}
        subLabel={<DeleteLabel name={deleteTarget?.label} />}
      />

      <FontNamingModal fonts={pendingFonts} onRename={renameFont} onSave={saveFonts} onCancel={discardFonts} />
    </AssetDropArea>
  );
}

export default FontAssets;
